import { FC, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { useParams } from '~/hooks/useParams';
import { useOrganizationByShortnameQuery } from '~/queries/organization/useOrganizationsQuery';
import { PlaceholderType } from './Placeholder';
import { OrganizationRestriction, RestrictionGuardType } from '../types';
import {
  passesOrganizationRestrictions,
  passesOrganizationRole,
  passesSubscription,
} from '../utils';

export const OrganizationSubscriptionRouteGuard: FC<{
  restrictions: readonly string[];
  mode: 'whitelist' | 'blacklist';
  type?: RestrictionGuardType;
}> = ({ restrictions, mode, ...props }) => {
  const { organizationShortName } = useParams<'organizationShortName'>();
  const { data: currentOrganization } = useOrganizationByShortnameQuery(
    { organizationShortName },
    { enabled: false },
  );

  const subscriptionId = currentOrganization?.subscription_tier_id;
  const pass = useMemo(
    () => passesSubscription({ restrictions, mode, subscriptionId }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subscriptionId],
  );

  return pass ? <Outlet /> : <PlaceholderType {...props} />;
};

export const OrganizationRoleRouteGuard: FC<{
  restriction: string;
  type?: RestrictionGuardType;
}> = ({ restriction, ...props }) => {
  const { organizationShortName } = useParams<'organizationShortName'>();
  const { data: currentOrganization } = useOrganizationByShortnameQuery(
    { organizationShortName },
    { enabled: false },
  );

  const pass = passesOrganizationRole({ restriction, roleId: currentOrganization?.roleId });
  return pass ? <Outlet /> : <PlaceholderType {...props} />;
};

export const OrganizationRouteGuard: FC<{
  restrictions: readonly OrganizationRestriction[];
  type?: RestrictionGuardType;
}> = ({ restrictions, ...props }) => {
  const { organizationShortName } = useParams<'organizationShortName'>();
  const { data: currentOrganization } = useOrganizationByShortnameQuery(
    { organizationShortName },
    { enabled: false },
  );

  const permissions = currentOrganization?.permissions;
  const pass = useMemo(
    () => passesOrganizationRestrictions({ restrictions, permissions }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissions],
  );

  return pass ? <Outlet /> : <PlaceholderType {...props} />;
};
